import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { adminService } from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';
import { normalizePlanKey } from '../../config/planFeatures';
import AccountsTable from './components/AccountsTable';
import AccountDetailModal from './components/AccountDetailModal';
import CreateAccountModal from '../super-admin-dashboard/components/CreateAccountModal';
import ImpersonateModal from '../super-admin-dashboard/components/ImpersonateModal';

const AccountManagementConsole = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/authentication');
      return;
    }

    checkAdminAndLoadData();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const accountId = searchParams?.get('id');
    if (accountId && accounts?.length > 0) {
      const account = accounts?.find(a => a?.id === accountId);
      if (account) {
        handleViewAccount(account);
      }
    }
  }, [searchParams, accounts]);

  useEffect(() => {
    filterAccounts();
  }, [accounts, searchQuery, filterPlan, filterStatus]);

  const checkAdminAndLoadData = async () => {
    try {
      setLoading(true);
      const adminStatus = await adminService?.isAdmin();
      
      if (!adminStatus) {
        navigate('/dashboard');
        return;
      }

      setIsAdmin(true);
      await loadAccounts();
    } catch (error) {
      console.error('Error checking admin status:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await adminService?.getAllAccounts();
      setAccounts(data);
      setFilteredAccounts(data);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const filterAccounts = () => {
    let filtered = [...accounts];

    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(account =>
        account?.email?.toLowerCase()?.includes(query) ||
        account?.fullName?.toLowerCase()?.includes(query) ||
        account?.tier?.toLowerCase()?.includes(query) ||
        account?.companyName?.toLowerCase()?.includes(query)
      );
    }

    if (filterPlan !== 'all') {
      filtered = filtered?.filter(account =>
        normalizePlanKey(account?.planType || account?.tier) === filterPlan
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered?.filter(account =>
        account?.subscriptionStatus?.toLowerCase() === filterStatus?.toLowerCase()
      );
    }

    setFilteredAccounts(filtered);
  };

  const handleViewAccount = async (account) => {
    try {
      const fullAccount = await adminService?.getAccountById(account?.id);
      setSelectedAccount(fullAccount);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error loading account details:', error);
    }
  };

  const handleUpdateAccount = async (accountId, updates) => {
    try {
      const updated = await adminService?.updateAccount(accountId, updates);
      // Refresh the full accounts list
      await loadAccounts();
      // Also update selectedAccount so the modal reflects the new values immediately
      if (updated) {
        setSelectedAccount(updated);
      } else {
        // Fallback: re-fetch the specific account
        try {
          const refreshed = await adminService?.getAccountById(accountId);
          if (refreshed) setSelectedAccount(refreshed);
        } catch (_) {}
      }
      return updated;
    } catch (error) {
      console.error('Error updating account:', error);
      throw error;
    }
  };

  if (loading || authLoading) {
    return (
      <NavigationBar stats={{}}>
        <div className="flex items-center justify-center min-h-screen">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </NavigationBar>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <NavigationBar stats={{}}>
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-3xl font-heading font-bold text-foreground">
                Account Management Console
              </h1>
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  onClick={() => setShowCreateModal(true)}
                >
                  <Icon name="UserPlus" size={18} className="mr-2" />
                  New Account
                </Button>
                <Button
                  variant="outline"
                   onClick={() => navigate('/pricing-plans-management')}
                >
                  <Icon name="DollarSign" size={18} className="mr-2" />
                  Manage Pricing
                </Button>
              </div>
            </div>
            <p className="text-muted-foreground">
              View, modify, and manage customer accounts
            </p>
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                placeholder="Search accounts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                icon="Search"
              />
              <Select
                value={filterPlan}
                onChange={setFilterPlan}
                options={[
                  { value: 'all', label: 'All Plans' },
                  { value: 'free', label: 'Free' },
                   { value: 'starter', label: 'Starter Pro' },
                   { value: 'professional', label: 'Business' },
                   { value: 'enterprise', label: 'Agency (White Label)' },
                ]}
              />
              <Select
                value={filterStatus}
                onChange={setFilterStatus}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                  { value: 'suspended', label: 'Suspended' }
                ]}
              />
            </div>
          </div>

          {/* Accounts Table */}
          <AccountsTable
            accounts={filteredAccounts}
            onViewAccount={handleViewAccount}
            onImpersonate={(account) => setImpersonateTarget(account)}
            onUpdateAccount={handleUpdateAccount}
          />
        </div>
      </div>

      {/* Account Detail Modal */}
      {showDetailModal && selectedAccount && (
        <AccountDetailModal
          account={selectedAccount}
          onClose={() => setShowDetailModal(false)}
          onUpdate={handleUpdateAccount}
        />
      )}

      {/* Create Account Modal */}
      {showCreateModal && (
        <CreateAccountModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={loadAccounts}
        />
      )}

      {/* Impersonate Modal */}
      {impersonateTarget && (
        <ImpersonateModal
          account={impersonateTarget}
          onClose={() => setImpersonateTarget(null)}
        />
      )}
    </NavigationBar>
  );
};

export default AccountManagementConsole;